/**
 * CR168: Bucket Source Editor Dialog
 * CR383: Added S0.5 config section (enable, mode, recipe, advanced JSON)
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { FlyerBucketSource, S05FlyerFetchMode } from "@/lib/flyer-bucket/types";
import { S05RecipeSelect } from "./S05RecipeSelect";

interface BucketSourceEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: FlyerBucketSource | null;
  onSave: (data: Omit<FlyerBucketSource, "id" | "created_at" | "updated_at">) => Promise<boolean>;
}

export function BucketSourceEditorDialog({
  open,
  onOpenChange,
  source,
  onSave,
}: BucketSourceEditorDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Basic fields
  const [name, setName] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [lookaheadDays, setLookaheadDays] = useState(7);
  const [allowFutureFlyers, setAllowFutureFlyers] = useState(true);

  // CR383: S0.5 fields
  const [s0Enabled, setS0Enabled] = useState(false);
  const [s0Mode, setS0Mode] = useState<S05FlyerFetchMode>("DOWNLOAD_ONLY");
  const [s0RecipeId, setS0RecipeId] = useState<string | null>(null);
  const [s0ConfigJson, setS0ConfigJson] = useState("{}");

  useEffect(() => {
    if (open) {
      if (source) {
        setName(source.name);
        setBucketName(source.bucket_name);
        setPrefix(source.prefix || "");
        setIsEnabled(source.is_enabled);
        setLookaheadDays(source.lookahead_days);
        setAllowFutureFlyers(source.allow_future_flyers);
        // CR383: Load S0.5 config
        setS0Enabled(source.s0_flyer_fetch_enabled);
        setS0Mode(source.s0_flyer_fetch_mode);
        setS0RecipeId(source.s0_flyer_fetch_recipe_id);
        setS0ConfigJson(JSON.stringify(source.s0_flyer_fetch_config ?? {}, null, 2));
      } else {
        setName("");
        setBucketName("flyer-pdfs");
        setPrefix("");
        setIsEnabled(true);
        setLookaheadDays(7);
        setAllowFutureFlyers(true);
        setS0Enabled(false);
        setS0Mode("DOWNLOAD_ONLY");
        setS0RecipeId(null);
        setS0ConfigJson("{}");
      }
    }
  }, [open, source]);

  const handleSave = async () => {
    if (!name.trim() || !bucketName.trim()) return;

    setSaving(true);
    try {
      // CR383: Parse advanced JSON config
      let parsedConfig: Record<string, unknown> = {};
      try {
        parsedConfig = JSON.parse(s0ConfigJson || "{}");
      } catch {
        toast({
          title: "Invalid JSON",
          description: "S0.5 config JSON is invalid, using previous config.",
          variant: "destructive",
        });
        parsedConfig = source?.s0_flyer_fetch_config ?? {};
      }

      const success = await onSave({
        name: name.trim(),
        bucket_name: bucketName.trim(),
        prefix: prefix.trim() || null,
        is_enabled: isEnabled,
        lookahead_days: lookaheadDays,
        allow_future_flyers: allowFutureFlyers,
        // CR383: S0.5 fields from form state
        s0_flyer_fetch_enabled: s0Enabled,
        s0_flyer_fetch_mode: s0Mode,
        s0_flyer_fetch_recipe_id: s0RecipeId,
        s0_flyer_fetch_config: parsedConfig,
      });

      if (success) {
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const isValid = name.trim() && bucketName.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{source ? "Edit Bucket Source" : "Add Bucket Source"}</DialogTitle>
          <DialogDescription>
            Configure a storage bucket for automated flyer discovery
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic fields */}
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly Flyers"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bucket">Bucket Name *</Label>
            <Input
              id="bucket"
              value={bucketName}
              onChange={(e) => setBucketName(e.target.value)}
              placeholder="e.g., flyer-pdfs"
            />
            <p className="text-xs text-muted-foreground">
              The storage bucket where flyer PDFs are stored
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prefix">Prefix (optional)</Label>
            <Input
              id="prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="e.g., 2026/weekly"
            />
            <p className="text-xs text-muted-foreground">
              Only scan files within this folder path
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lookahead">Lookahead Days</Label>
              <Input
                id="lookahead"
                type="number"
                min={1}
                max={30}
                value={lookaheadDays}
                onChange={(e) => setLookaheadDays(parseInt(e.target.value) || 7)}
              />
              <p className="text-xs text-muted-foreground">
                How far ahead to include future flyers
              </p>
            </div>

            <div className="space-y-2 flex flex-col justify-end">
              <div className="flex items-center justify-between">
                <Label htmlFor="future">Allow Future</Label>
                <Switch
                  id="future"
                  checked={allowFutureFlyers}
                  onCheckedChange={setAllowFutureFlyers}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Include flyers that start in the future
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <Label htmlFor="enabled">Enabled</Label>
            <Switch
              id="enabled"
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
            />
          </div>

          {/* CR383: S0.5 Download Flyer section */}
          <section className="space-y-3 border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">S0.5 – Download Flyer</h4>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Optional pre-step to fetch flyers via Python before S1.0/S1.1.
                </p>
              </div>
              <Switch
                checked={s0Enabled}
                onCheckedChange={setS0Enabled}
              />
            </div>

            {s0Enabled && (
              <>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Mode</Label>
                    <Select
                      value={s0Mode}
                      onValueChange={(v) => setS0Mode(v as S05FlyerFetchMode)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DOWNLOAD_ONLY">
                          Download only (S0.5 → S1.0 → S1.1)
                        </SelectItem>
                        <SelectItem value="IMAGE_ONLY">
                          Screenshot only (S0.5 → S1.2)
                        </SelectItem>
                        <SelectItem value="IMAGE_AND_DOWNLOAD">
                          Both (download + screenshots)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs">Flyer Fetch Recipe</Label>
                    <S05RecipeSelect
                      value={s0RecipeId}
                      onChange={setS0RecipeId}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Recipes from <code className="text-[10px]">capture_recipe_templates</code> (scope = FLYER_FETCH).
                    </p>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Advanced config (JSON)</Label>
                  <Textarea
                    className="font-mono text-xs min-h-[100px]"
                    value={s0ConfigJson}
                    onChange={(e) => setS0ConfigJson(e.target.value)}
                    placeholder="{}"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Passed to the Python worker as <code className="text-[10px]">recipe.config</code>
                    {" "}(selectors, pagination, timeouts, etc).
                  </p>
                </div>
              </>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !isValid}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {source ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
